<template>
  <div class="review-step">

    <!-- ── Loan Summary ── -->
    <div class="review-section">
      <p class="fm-section-label">Loan Summary</p>
      <div class="review-grid">
        <div class="review-item">
          <span class="review-item__label">Amount</span>
          <span class="review-item__value">{{ readonlyInfo.loan.amount }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">Duration</span>
          <span class="review-item__value">{{ readonlyInfo.loan.terms }} Month{{ readonlyInfo.loan.terms > 1 ? 's' : '' }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">Type</span>
          <span class="review-item__value">{{ readonlyInfo.loan.type }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">{{ readonlyInfo.loan.frequency | capitalize }} Repayment</span>
          <span v-if="repaymentAmount !== null" class="review-item__value review-item__value--highlight">{{ repaymentAmount }} <span class="review-item__approx">approx.</span></span>
          <span v-else class="review-item__value text-danger">Error</span>
        </div>
      </div>
    </div>

    <!-- ── Fees ── -->
    <div class="review-section review-section--fees">
      <p class="fm-section-label">{{ isSACC ? 'Mini Flash Loan Fees (SACC)' : 'Super Flash Loan Fees (MACC)' }}</p>

      <template v-if="isSACC">
        <div class="fee-row">
          <span>Establishment Fee <span class="fee-note">once off per loan</span></span>
          <strong>20% of principal</strong>
        </div>
        <div class="fee-row">
          <span>Monthly Loan Fee <span class="fee-note">per month while balance outstanding</span></span>
          <strong>4% of principal/month</strong>
        </div>
        <div class="fee-row fee-row--muted">
          <span>Dishonour Fee</span>
          <span><Money :amount="vars.dishonor_charge ?? 20"/></span>
        </div>
        <div class="fee-row fee-row--muted">
          <span>Default Fee <span class="fee-note">daily</span></span>
          <span><Money :amount="vars.default_charge ?? 5"/></span>
        </div>
      </template>

      <template v-else>
        <div class="fee-row">
          <span>Establishment Fee</span>
          <strong>$400.00</strong>
        </div>
        <div class="fee-row">
          <span>Interest Rate (APR)</span>
          <strong>47.6158% p.a.</strong>
        </div>
        <div class="fee-row">
          <span>Comparison Rate</span>
          <strong>65.6597% p.a.</strong>
        </div>
        <div class="fee-row fee-row--muted">
          <span>Dishonour Fee</span>
          <span><Money :amount="vars.dishonor_charge ?? 20"/></span>
        </div>
        <div class="fee-row fee-row--muted">
          <span>Reschedule Fee</span>
          <span><Money :amount="vars.reschedule_charge ?? 20"/></span>
        </div>
      </template>
    </div>

    <!-- ── Your Details ── -->
    <div class="review-section">
      <p class="fm-section-label">Your Details</p>
      <div class="review-grid">
        <div class="review-item">
          <span class="review-item__label">Full Name</span>
          <span class="review-item__value">{{ readonlyInfo.user.fullName }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">Date of Birth</span>
          <span class="review-item__value">{{ readonlyInfo.user.dob }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">Email</span>
          <span class="review-item__value">{{ readonlyInfo.user.email }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">Mobile</span>
          <span class="review-item__value">{{ readonlyInfo.user.mobile }}</span>
        </div>
        <div class="review-item review-item--full">
          <span class="review-item__label">Address</span>
          <span class="review-item__value">{{ readonlyInfo.user.addresses[0] }}</span>
        </div>
      </div>
    </div>

    <!-- ── Banking ── -->
    <div class="review-section">
      <p class="fm-section-label">Banking Information</p>
      <div class="review-grid">
        <div class="review-item">
          <span class="review-item__label">Account Name</span>
          <span class="review-item__value">{{ readonlyInfo.fundDistribution.name }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">BSB</span>
          <span class="review-item__value">{{ readonlyInfo.fundDistribution.bsb }}</span>
        </div>
        <div class="review-item">
          <span class="review-item__label">Account Number</span>
          <span class="review-item__value">{{ readonlyInfo.fundDistribution.number }}</span>
        </div>
      </div>
    </div>

    <!-- ── ID ── -->
    <div class="review-section">
      <p class="fm-section-label">Identity Verification</p>
      <div class="review-grid">
        <template v-if="readonlyInfo.idVerification.documentType === 'passport'">
          <div class="review-item">
            <span class="review-item__label">Passport Number</span>
            <span class="review-item__value">{{ readonlyInfo.idVerification.passport.number }}</span>
          </div>
          <div class="review-item">
            <span class="review-item__label">Country</span>
            <span class="review-item__value">{{ readonlyInfo.idVerification.passport.country }}</span>
          </div>
        </template>
        <template v-else>
          <div class="review-item">
            <span class="review-item__label">Licence Number</span>
            <span class="review-item__value">{{ readonlyInfo.idVerification.license.number }}</span>
          </div>
          <div class="review-item">
            <span class="review-item__label">Card Number</span>
            <span class="review-item__value">{{ readonlyInfo.idVerification.license.cardNumber }}</span>
          </div>
          <div class="review-item">
            <span class="review-item__label">State</span>
            <span class="review-item__value">{{ readonlyInfo.idVerification.license.state }}</span>
          </div>
        </template>

        <template v-if="isAustralianCitizen">
          <div class="review-item">
            <span class="review-item__label">Medicare Card</span>
            <span class="review-item__value">{{ readonlyInfo.idVerification.medicare.number }}</span>
          </div>
          <div class="review-item">
            <span class="review-item__label">Medicare Reference</span>
            <span class="review-item__value">{{ readonlyInfo.idVerification.medicare.reference }}</span>
          </div>
        </template>
      </div>

      <p class="review-legal-note">
        This guide does not constitute legal advice. Before making a decision, visit
        <a href="https://moneysmart.gov.au/" target="_blank">moneysmart.gov.au</a> or speak to your financial advisor.
      </p>
    </div>

    <!-- ── Declarations ── -->
    <div class="review-declarations">
      <p class="fm-section-label">Declarations & Consent</p>

      <Checkbox v-model="form.declareAccurate" :error-container="errorContainerName" name="declareAccurate">
        <template v-slot="{ onClick }"><div @click="onClick">I declare that the information I have provided is true and accurate.</div></template>
      </Checkbox>

      <Checkbox v-model="form.consentThirdParty" :error-container="errorContainerName" name="consentThirdParty">
        <template v-slot="{ onClick }">
          <div @click="onClick">I confirm that I am authorised to provide the personal details presented and I consent to my information being checked via third party systems for identity confirmation.</div>
        </template>
      </Checkbox>

      <Checkbox v-model="form.subscribeOffers" :error-container="errorContainerName" name="subscribeOffers">
        <template v-slot="{ onClick }">
          <div @click="onClick">
            I would like to hear from Flash Money about loan offers and debt management services that may interest me.
          </div>
        </template>
      </Checkbox>

      <Checkbox v-model="form.agreeTermsAndConditions" :error-container="errorContainerName" name="agreeTermsAndConditions">
        <template v-slot="{ onClick }">
          <div @click="onClick">
            I agree to the Terms and Conditions of
            <a class="review-link" @click.stop href="/docs/Flash Money Direct Debit Authority &amp; Service Agreement.pdf" target="_blank">Flash Money's Direct Debit Request and Service Agreement</a>.
          </div>
        </template>
      </Checkbox>

      <Checkbox v-model="form.legal" :error-container="errorContainerName" name="legal">
        <template v-slot="{ onClick }">
          <div @click="onClick" class="d-flex flex-column" style="gap: 6px;">
            <strong>Legal Declaration</strong>
            <p style="margin:0; font-size: 13px;">
              I acknowledge that a person who commits fraud by deception or makes a false document is guilty of a
              criminal offence and will be referred to the relevant law enforcement authority and criminally prosecuted.
            </p>
            <p style="margin:0; font-size: 13px;">
              I solemnly declare that all information provided in my application to Flash Money Pty Ltd is truthful,
              correct and accurate.
            </p>
          </div>
        </template>
      </Checkbox>

      <Checkbox v-model="form.jurisdiction" :error-container="errorContainerName" name="jurisdiction">
        <template v-slot="{ onClick }">
          <div @click="onClick" class="d-flex flex-column" style="gap: 6px;">
            <strong>Jurisdiction</strong>
            <p style="margin:0; font-size: 13px;">
              All parties agree that the courts of New South Wales, Australia shall have exclusive jurisdiction to
              settle any dispute arising in connection with this agreement.
            </p>
          </div>
        </template>
      </Checkbox>

      <Checkbox v-model="form.privacyPolicy" :error-container="errorContainerName" name="privacyPolicy">
        <template v-slot="{ onClick }">
          <div @click="onClick">
            I have reviewed the
            <a class="review-link" @click.stop href="/docs/Privacy Policy - Credit Providers (APP entities).pdf" target="_blank">Privacy Policy</a>
            and I understand and accept all terms it sets out.
          </div>
        </template>
      </Checkbox>

      <Checkbox v-model="form.acknowledgementAndConsent" :error-container="errorContainerName" name="acknowledgementAndConsent">
        <template v-slot="{ onClick }">
          <div @click="onClick">
            I have read and understood the
            <a @click.stop class="review-link" href="/docs/Acknowledgement and Consent.pdf" target="_blank">Acknowledgement and Consent</a>
            and make all consents it contains, including consenting to: electronic identity verification, promotion of
            products and services, information exchange with other providers, and a credit check.
          </div>
        </template>
      </Checkbox>
    </div>

    <slot name="controls" v-bind="{ prev: onPrev, next: onNext }"/>
  </div>
</template>

<script>
import FormGroup from '@modules/shared/components/form/FormGroup';
import {NEW_APPLICATION} from '@store/constants';
import {ApplicationChannel} from '@modules/guest/new-applicant/constants';
import NewApplicationStepMixin from '@modules/guest/new-applicant/components/mixins/NewApplicationStepMixin';
import {validate} from './formValidator';
import ErrorText from '@modules/shared/components/form/ErrorText';
import {scrollToErrorFormField} from '@modules/shared/helpers/form';
import store from '@modules/guest/new-applicant/store/store';
import {saveAsDraft} from '@modules/guest/new-applicant/NewApplicationService';
import Checkbox from '@modules/shared/components/form/FmCheckbox.vue';
import Money from '@components/common/Money.vue';

const ERROR_CONTAINER_NAME = 'NewApplicationReviewStepForm';

export default {
  name: 'Review',
  mixins: [NewApplicationStepMixin],
  components: {Money, Checkbox, ErrorText, FormGroup},
  filters: {
    capitalize(val) { return val ? val.charAt(0).toUpperCase() + val.slice(1) : val; },
  },
  data() {
    const defaults = {
      declareAccurate: false, consentThirdParty: false, subscribeOffers: false,
      agreeTermsAndConditions: false, legal: false, jurisdiction: false,
      privacyPolicy: false, acknowledgementAndConsent: false,
    };
    const cache = store.state[NEW_APPLICATION.MODULE_NAME].cache;
    const isTopUp = cache?.channel === ApplicationChannel.CUSTOMER_PORTAL_TOP_UP;
    const isReApply = cache?.channel === ApplicationChannel.CUSTOMER_PORTAL_RE_APPLY;
    const stored = store.state[NEW_APPLICATION.MODULE_NAME].form.Review || {};
    return { form: (isTopUp || isReApply) ? { ...defaults, ...stored } : { ...defaults } };
  },
  computed: {
    code() { return store.state[NEW_APPLICATION.MODULE_NAME].code; },
    vars() { return this.$store.getters[NEW_APPLICATION.GETTERS.VARS]; },
    readonlyInfo() { return this.$store.getters[NEW_APPLICATION.GETTERS.READONLY_INFO]; },
    repaymentAmount() {
      const amount = this.readonlyInfo.loan.repaymentAmount;
      return typeof amount !== 'number' ? null : `$${Number(amount).toFixed(2)}`;
    },
    isSACC() { return this.$store.getters[NEW_APPLICATION.GETTERS.IS_SACC]; },
    isAustralianCitizen() { return this.$store.getters[NEW_APPLICATION.GETTERS.IS_AUSTRALIAN_RESIDENT]; },
  },
  watch: {
    form: {
      deep: true, immediate: true,
      handler() { this.shouldGoNextStep(); this.syncWithStore(); },
    },
  },
  methods: {
    onNext() {
      if (!this.shouldGoNextStep()) { scrollToErrorFormField(this.$el); return; }
      this.saveAndSync().then(async () => {
        await store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP);
      }).catch(e => { this.handleServerError(e); scrollToErrorFormField(this.$el); });
    },
    async saveAndSync() {
      const payload = { step: 'Review', ...this.form };
      store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: true}});
      try {
        const {data: {draft, otp, loanNumber, canPerformCS}} = await saveAsDraft(this.code, payload);
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_META, payload: {otp, loanNumber, canPerformCS}});
        store.commit({type: NEW_APPLICATION.MUTATIONS.SET_CACHE, payload: draft});
      } catch (e) { throw e; }
      finally { store.commit({type: NEW_APPLICATION.MUTATIONS.SET_STEPS_META, payload: {loading: false}}); }
    },
    onPrev() { store.dispatch(NEW_APPLICATION.ACTIONS.PREV_STEP); },
    syncWithStore() {
      this.$store.commit({ type: NEW_APPLICATION.MUTATIONS.SET_REVIEW, payload: { ...this.form } });
    },
    validate,
  },
  created() { this.registerErrorContainer(ERROR_CONTAINER_NAME); },
};
</script>

<style lang="scss">
$amber:    #FFB000;
$ink:      #15171C;
$ink-soft: #5B606B;
// 1.4.3 Contrast Minimum — #9197A1 on the surrounding #fff/#FBFAF6 backgrounds
// measured ~2.63–2.94:1 (fails the 4.5:1 text minimum). #6B7280 measures
// ~5.3:1+ on both backgrounds while keeping the same "faint/secondary" role.
$ink-faint:#6B7280;
$line:     #E7E4DA;
$surface:  #FBFAF6;

.review-step {
  .review-section {
    border: 1px solid $line;
    border-radius: 14px;
    padding: 1.2rem 1.4rem 0.8rem;
    margin-bottom: 1.25rem;
    background: #fff;

    &--fees {
      background: $surface;
    }
  }

  .review-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 1rem 1.25rem;
  }

  .review-item {
    display: flex;
    flex-direction: column;
    gap: 3px;

    &--full { grid-column: 1 / -1; }

    &__label {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: $ink-faint;
    }

    &__value {
      font-size: 14.5px;
      font-weight: 600;
      color: $ink;
      word-break: break-word;

      &--highlight {
        font-size: 1.2rem;
        color: $ink;
      }
    }

    &__approx {
      font-size: 11px;
      font-weight: 400;
      color: $ink-faint;
    }
  }

  .fee-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 8px 0;
    border-bottom: 1px solid $line;
    font-size: 13.5px;
    gap: 1rem;

    &:last-child { border-bottom: none; }

    &--muted { color: $ink-soft; }

    .fee-note {
      font-size: 11.5px;
      color: $ink-faint;
      display: block;
    }

    strong { font-weight: 600; color: $ink; white-space: nowrap; }
  }

  .review-legal-note {
    font-size: 12.5px;
    color: $ink-faint;
    margin: 1rem 0 0;
    line-height: 1.5;

    a { color: $ink-soft; text-decoration: underline; }
  }

  .review-declarations {
    margin-bottom: 1.5rem;
  }

  // 1.4.3 Contrast Minimum — darken(#FFB000, 15%) resolves to ~#cc8c00, which
  // measures ~2.98:1 on white (fails the 4.5:1 text minimum, and these are
  // underlined links to legal documents). #7a5c00 measures ~5.4:1 on white
  // and matches the same fix applied to bought-lead's LeadLoanApplication.vue.
  .review-link {
    color: #7a5c00;
    text-decoration: underline;
    font-weight: 500;
  }
}
</style>
